import { useTheme } from '@mui/material';
import { Chart as ChartJS, ChartOptions } from 'chart.js';
import { useMemo } from 'react';
import { useAppSelector } from '../../../app/hooks';
import { FeatureEnum } from '../../../services/data/impl/cube/cubeSlice';
import { useBlueberryEstimatedCountRowsideStackBar } from '../../scanDataApi/hooks/useBlueberryEstimatedCountRowsideStackBar';
import { selectCurrBlueberryClass } from '../../select/selectSlice';
import { useChartFeatureMeasureLabels } from './useChartLabels';

export const useBlueberryEstimatedCountRowsideStackBarChart = (
  chart?: ChartJS<'bar', number[], unknown> | null,
  plugins?: ChartOptions['plugins']
) => {
  const theme = useTheme();
  // Get the x and y axis labels for the current chart feature and dimension
  const labelResult = useChartFeatureMeasureLabels();
  const currBlueberryClass = useAppSelector(selectCurrBlueberryClass);
  const { data } = useBlueberryEstimatedCountRowsideStackBar({
    activeFilters: {
      currBlueberryClass: true,
    },
  });

  const dataFiltered = useMemo(() => {
    if (!data) return null;
    // Multi-stack bar chart only happens when currBlueberryClass is FeatureEnum.blueberry, FeatureEnum.flower or null.
    // If currBlueberryClass is not one of those options, it's a single stack bar chart.
    if (
      currBlueberryClass &&
      currBlueberryClass !== FeatureEnum.blueberry &&
      currBlueberryClass !== FeatureEnum.flower
    ) {
      return { ...data };
    }

    // Ensure datasets is always an array, never null
    const newData = {
      ...data,
      datasets: data.datasets ? [...data.datasets] : [],
    };

    return newData;
  }, [data, currBlueberryClass]);

  const options: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      ...plugins,
    },
    scales: {
      y: {
        stacked: true,
        beginAtZero: true,
        min: 0,
        ticks: {
          color: theme.colors.common.white,
        },
        grid: {
          display: true,
          color: theme.colors.darkTheme.grey.muted,
        },
        title: {
          display: true,
          text: labelResult ? labelResult.yLabel : '',
          color: theme.colors.common.white,
        },
      },
      x: {
        stacked: true,
        ticks: {
          color: theme.colors.common.white,
          font: {
            size: 12,
          },
        },
        grid: {
          display: false,
        },
        title: {
          display: true,
          text: labelResult ? labelResult.xLabel : '',
          color: theme.colors.common.white,
        },
      },
    },
    events: ['click', 'mousemove'] as Array<keyof HTMLElementEventMap>,
  };

  const result = useMemo(
    () => ({
      data: dataFiltered,
      options,
    }),
    [dataFiltered, options]
  );

  return result;
};
